import React, { useEffect, useState } from "react";
import SiteService from "services/SiteService";
import {
  Button,
  Table,
  OverlayTrigger,
  Popover,
  Spinner,
} from "react-bootstrap";
import { format } from "date-fns";
import { toast } from "react-toastify";
import {
  SITE_SHARING_HAS_BEEN_PAUSED,
  SITE_HAS_BEEN_RE_SHARED,
  ERROR_UPDATING_SHARE_DETAILS,
  ERROR_FETCHING_SHARED_SITES,
} from "configurations/MessageConstants";
import EmptyShare from "components/common/EmptyShare";

const SharedDetails = ({ site }) => {
  const [sharedDetails, setSharedDetails] = useState([]);
  const [updatingShareId, setUpdatingShareId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const getSharedSites = async () => {
      try {
        setIsLoading(true);
        const response = await SiteService.getSharedSites(site.id);
        setSharedDetails(response.items);
      } catch (error) {
        console.error(ERROR_FETCHING_SHARED_SITES, error);
        toast.error(error.response.data.detail);
      } finally {
        setIsLoading(false);
      }
    };
    getSharedSites();
  }, [site]);

  const handleUpdateShare = async (share) => {
    try {
      setUpdatingShareId(share.id);
      const payload = {
        active: !share.active,
      };
      const response = await SiteService.updateShare(
        share.id,
        share.siteId,
        payload
      );
      const updatedDetails = sharedDetails.map((item) =>
        item.id === response.id ? response : item
      );
      setSharedDetails(updatedDetails);
      if (share.active) {
        toast.success(`${SITE_SHARING_HAS_BEEN_PAUSED}`);
      } else {
        toast.success(`${SITE_HAS_BEEN_RE_SHARED}`);
      }
    } catch (error) {
      console.error(ERROR_UPDATING_SHARE_DETAILS, error);
      toast.error(`${ERROR_UPDATING_SHARE_DETAILS}`);
    } finally {
      setUpdatingShareId(null);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), "MM/dd/yyyy");
  };

  return (
    <div className="d-flex flex-column gap-2">
      {isLoading ? (
        <div className="loader-container">
          <Spinner animation="border" variant="dark" size="lg" />
        </div>
      ) : sharedDetails.length === 0 ? (
        <div className="text-center text-muted">
          <EmptyShare
            emptyText={
              <>
                <b>{site.name}</b> is not shared with any user.
              </>
            }
          />
        </div>
      ) : (
        <div>
          <h6 className="my-3 text-muted text-start">
            <b>{site.name}</b> has been shared with:
          </h6>
          <Table
            bordered
            hover
            responsive
            className="share-container text-nowrap text-center"
          >
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Actions</th>
                <th>Info</th>
              </tr>
            </thead>
            <tbody>
              {sharedDetails.map((user) => (
                <tr key={user.id} className="small">
                  <td>{user.userName}</td>
                  <td>{user.userEmail}</td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleUpdateShare(user)}
                      className="w-100"
                      disabled={updatingShareId === user.id}
                      aria-label="Update Share"
                    >
                      {updatingShareId === user.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : user.active ? (
                        <>
                          <i
                            className="fa fa-pause me-1"
                            aria-hidden="true"
                          ></i>
                        </>
                      ) : (
                        <>
                          <i
                            className="fa fa-refresh me-1"
                            aria-hidden="true"
                          ></i>
                        </>
                      )}
                    </Button>
                  </td>
                  <td className="text-center">
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      placement="top"
                      overlay={
                        <Popover id={`share-info-${user.id}`}>
                          <Popover.Body>
                            <div>
                              <strong>Shared on</strong>{" "}
                              {formatDate(user.created)}
                              {user.created !== user.updated && (
                                <>
                                  <br />
                                  <strong>Updated on</strong>{" "}
                                  {formatDate(user.updated)}
                                </>
                              )}
                            </div>
                          </Popover.Body>
                        </Popover>
                      }
                      aria-label="Share Info"
                    >
                      <span>
                        <i
                          className="fa fa-info-circle mt-2"
                          aria-hidden="true"
                        ></i>
                      </span>
                    </OverlayTrigger>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default SharedDetails;
